import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { joinWaitlist } from '../services/supabase';
import { ROUTES } from '../constants';
import DemoChat from '../components/DemoChat';
import {
    Sparkles,
    Heart,
    MessageCircle,
    ArrowRight,
    Play,
    CheckCircle,
    User,
    Key,
    Briefcase,
    Users,
    Loader2
} from 'lucide-react';

const Landing: React.FC = () => {
    const [showSignIn, setShowSignIn] = useState(false);
    const [showInviteCode, setShowInviteCode] = useState(false);
    const [showWaitlist, setShowWaitlist] = useState(false);
    const [activeUseCase, setActiveUseCase] = useState('professional');

    // Form states
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [inviteCode, setInviteCodeValue] = useState('');
    const [waitlistEmail, setWaitlistEmail] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [successMessage, setSuccessMessage] = useState('');

    const { signIn, signUpWithInvite, signInWithGoogle } = useAuth();
    const navigate = useNavigate();

    const scrollToTop = () => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const resetForm = () => {
        setEmail('');
        setPassword('');
        setInviteCodeValue('');
        setWaitlistEmail('');
        setError('');
        setSuccessMessage('');
        setLoading(false);
    };

    const handleSignIn = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true);
        setError('');

        try {
            await signIn(email, password);
            navigate(ROUTES.DASHBOARD);
        } catch (error) {
            setError(error instanceof Error ? error.message : 'An error occurred');
        } finally {
            setLoading(false);
        }
    };

    const handleSignUpWithInvite = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true);
        setError('');

        try {
            await signUpWithInvite(email, password, inviteCode);
            navigate(ROUTES.DASHBOARD);
        } catch (error) {
            setError(error instanceof Error ? error.message : 'An error occurred');
        } finally {
            setLoading(false);
        }
    };

    const handleGoogleSignIn = async () => {
        setLoading(true);
        setError('');

        try {
            await signInWithGoogle();
            // Note: No need to navigate here as OAuth will redirect
        } catch (error) {
            setError(error instanceof Error ? error.message : 'An error occurred');
            setLoading(false);
        }
    };

    const handleJoinWaitlist = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true);
        setError('');
        setSuccessMessage('');

        try {
            await joinWaitlist(waitlistEmail);
            setSuccessMessage('Successfully joined the waitlist! We\'ll be in touch soon.');
            setWaitlistEmail('');
        } catch (error) {
            setError(error instanceof Error ? error.message : 'An error occurred');
        } finally {
            setLoading(false);
        }
    };

    const closeModals = () => {
        setShowSignIn(false);
        setShowInviteCode(false);
        setShowWaitlist(false);
        resetForm();
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50">
            {/* Navigation */}
            <nav className="relative z-50 px-6 py-4 bg-white/80 backdrop-blur-md border-b border-slate-200/50 sticky top-0">
                <div className="max-w-7xl mx-auto flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                        <div className="w-10 h-10 bg-gradient-to-br from-blue-600 to-purple-600 rounded-xl flex items-center justify-center">
                            <Sparkles className="w-6 h-6 text-white" />
                        </div>
                        <span className="text-2xl font-bold bg-gradient-to-r from-slate-900 to-slate-700 bg-clip-text text-transparent">
                            Echoo.ai
                        </span>
                    </div>
                    <div className="flex items-center space-x-4">
                        <button
                            onClick={() => setShowInviteCode(true)}
                            className="flex items-center space-x-2 text-slate-600 hover:text-slate-900 transition-colors font-medium"
                        >
                            <Key className="w-4 h-4" />
                            <span>Have an invite?</span>
                        </button>
                        <button
                            onClick={() => setShowSignIn(true)}
                            className="flex items-center space-x-2 text-slate-600 hover:text-slate-900 transition-colors font-medium"
                        >
                            <User className="w-4 h-4" />
                            <span>Sign In</span>
                        </button>
                        <button
                            onClick={() => setShowWaitlist(true)}
                            className="bg-gradient-to-r from-blue-600 to-purple-600 text-white px-6 py-3 rounded-xl font-semibold hover:from-blue-700 hover:to-purple-700 transition-all duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
                        >
                            Join Waitlist
                        </button>
                    </div>
                </div>
            </nav>

            {/* Hero Section */}
            <section className="relative px-6 py-20">
                <div className="max-w-6xl mx-auto">
                    <div className="text-center mb-12">
                        <h1 className="hero-text text-4xl md:text-6xl font-bold text-slate-900 mb-6 leading-tight">
                            Create your
                            <span className="bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent"> Echoo</span>,
                            <br />
                            Your authentic digital twin
                        </h1>

                        <p className="tagline text-xl md:text-2xl text-slate-600 max-w-3xl mx-auto mb-8">
                            Connect with your audiences, represent you authentically, <br />anywhere, anytime.
                        </p>

                        <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-8">
                            <button
                                onClick={() => setShowWaitlist(true)}
                                className="bg-gradient-to-r from-blue-600 to-purple-600 text-white px-8 py-4 rounded-xl text-lg font-semibold hover:from-blue-700 hover:to-purple-700 transition-all duration-200 shadow-lg hover:shadow-xl"
                            >
                                Join the Waitlist
                            </button>
                        </div>

                        <div className="flex justify-center items-center gap-6 text-slate-500 text-sm">
                            <span>🔒 Early access</span>
                            <span>✨ AI-powered</span>
                            <span>✅ You stay in control</span>
                        </div>
                    </div>

                    {/* Simple Demo */}
                    {/* <div className="max-w-4xl mx-auto">
                        <div className="bg-white rounded-2xl shadow-xl border border-slate-200 overflow-hidden">
                        <DemoChat />
                    </div>
                    </div> */}
                </div>
            </section >

            {/* Simple Benefits Section */}
            < section className="px-6 py-16 bg-slate-50" >
                <div className="max-w-4xl mx-auto text-center">
                    <h2 className="text-3xl font-bold text-slate-900 mb-12">
                        Your Echoo, your way
                    </h2>

                    <div className="grid md:grid-cols-3 gap-8">
                        <div className="bg-white p-6 rounded-xl shadow-sm">
                            <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mx-auto mb-4">
                                <Heart className="w-6 h-6 text-blue-600" />
                            </div>
                            <h3 className="text-lg font-semibold text-slate-900 mb-2">Always You</h3>
                            <p className="text-slate-600">
                                Built from your stories and experiences, authentically representing who you are
                            </p>
                        </div>

                        <div className="bg-white p-6 rounded-xl shadow-sm">
                            <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center mx-auto mb-4">
                                <MessageCircle className="w-6 h-6 text-purple-600" />
                            </div>
                            <h3 className="text-lg font-semibold text-slate-900 mb-2">Always Available</h3>
                            <p className="text-slate-600">
                                Your Echoo is there 24/7, connecting with people even when you can't
                            </p>
                        </div>

                        <div className="bg-white p-6 rounded-xl shadow-sm">
                            <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center mx-auto mb-4">
                                <CheckCircle className="w-6 h-6 text-green-600" />
                            </div>
                            <h3 className="text-lg font-semibold text-slate-900 mb-2">Always in Control</h3>
                            <p className="text-slate-600">
                                You choose what to share, who can access, and modify or delete anytime
                            </p>
                        </div>
                    </div>
                </div>
            </section >
            {/* Interactive Use Cases Section */}
            < section className="px-6 py-16 bg-white" >
                <div className="max-w-6xl mx-auto">
                    <div className="text-center mb-12">
                        <h2 className="text-3xl font-bold text-slate-900 mb-4">
                            Perfect for work, life, and everything in between
                        </h2>
                        <p className="text-lg text-slate-600 max-w-2xl mx-auto">
                            Get inspired by the different ways to build Echoo.
                        </p>
                    </div>

                    {/* Interactive Tabs */}
                    <div className="flex justify-center mb-8">
                        <div className="bg-slate-100 p-1 rounded-xl flex">
                            <button
                                onClick={() => setActiveUseCase('professional')}
                                className={`px-6 py-3 rounded-lg font-medium transition-all ${activeUseCase === 'professional'
                                    ? 'bg-white text-slate-900 shadow-sm'
                                    : 'text-slate-600 hover:text-slate-900'
                                    }`}
                            >
                                <Briefcase className="w-4 h-4 inline mr-2" />
                                Professional
                            </button>
                            <button
                                onClick={() => setActiveUseCase('personal')}
                                className={`px-6 py-3 rounded-lg font-medium transition-all ${activeUseCase === 'personal'
                                    ? 'bg-white text-slate-900 shadow-sm'
                                    : 'text-slate-600 hover:text-slate-900'
                                    }`}
                            >
                                <Heart className="w-4 h-4 inline mr-2" />
                                Personal
                            </button>
                            <button
                                onClick={() => setActiveUseCase('creative')}
                                className={`px-6 py-3 rounded-lg font-medium transition-all ${activeUseCase === 'creative'
                                    ? 'bg-white text-slate-900 shadow-sm'
                                    : 'text-slate-600 hover:text-slate-900'
                                    }`}
                            >
                                <Sparkles className="w-4 h-4 inline mr-2" />
                                Creative
                            </button>
                        </div>
                    </div>

                    {/* Content Based on Active Tab */}
                    <div className="grid lg:grid-cols-2 gap-12 items-center">
                        <div>
                            {activeUseCase === 'professional' && (
                                <div className="space-y-6">
                                    <h3 className="text-2xl font-bold text-slate-900">Build a professional Echoo</h3>
                                    <p className="text-lg text-slate-600">
                                        Create an Echoo that showcases your expertise, work style, and professional personality.
                                    </p>
                                    <div className="space-y-4">
                                        <div className="flex items-start space-x-3">
                                            <div className="w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                                                <CheckCircle className="w-4 h-4 text-blue-600" />
                                            </div>
                                            <p className="text-slate-600">Share your professional experience and approach</p>
                                        </div>
                                        <div className="flex items-start space-x-3">
                                            <div className="w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                                                <CheckCircle className="w-4 h-4 text-blue-600" />
                                            </div>
                                            <p className="text-slate-600">Include your values and working style</p>
                                        </div>
                                        <div className="flex items-start space-x-3">
                                            <div className="w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                                                <CheckCircle className="w-4 h-4 text-blue-600" />
                                            </div>
                                            <p className="text-slate-600">Highlight your unique perspectives and insights</p>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {activeUseCase === 'personal' && (
                                <div className="space-y-6">
                                    <h3 className="text-2xl font-bold text-slate-900">Build a personal Echoo</h3>
                                    <p className="text-lg text-slate-600">
                                        Create an Echoo filled with your personal stories, memories, and the things that make you uniquely you.
                                    </p>
                                    <div className="space-y-4">
                                        <div className="flex items-start space-x-3">
                                            <div className="w-6 h-6 bg-purple-100 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                                                <CheckCircle className="w-4 h-4 text-purple-600" />
                                            </div>
                                            <p className="text-slate-600">Share your life stories and experiences</p>
                                        </div>
                                        <div className="flex items-start space-x-3">
                                            <div className="w-6 h-6 bg-purple-100 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                                                <CheckCircle className="w-4 h-4 text-purple-600" />
                                            </div>
                                            <p className="text-slate-600">Include your values, beliefs, and personality</p>
                                        </div>
                                        <div className="flex items-start space-x-3">
                                            <div className="w-6 h-6 bg-purple-100 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                                                <CheckCircle className="w-4 h-4 text-purple-600" />
                                            </div>
                                            <p className="text-slate-600">Add your interests, hobbies, and passions</p>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {activeUseCase === 'creative' && (
                                <div className="space-y-6">
                                    <h3 className="text-2xl font-bold text-slate-900">Build a creative Echoo</h3>
                                    <p className="text-lg text-slate-600">
                                        Create an Echoo that captures your artistic journey, creative process, and the inspiration behind your work.
                                    </p>
                                    <div className="space-y-4">
                                        <div className="flex items-start space-x-3">
                                            <div className="w-6 h-6 bg-orange-100 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                                                <CheckCircle className="w-4 h-4 text-orange-600" />
                                            </div>
                                            <p className="text-slate-600">Document your creative process and techniques</p>
                                        </div>
                                        <div className="flex items-start space-x-3">
                                            <div className="w-6 h-6 bg-orange-100 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                                                <CheckCircle className="w-4 h-4 text-orange-600" />
                                            </div>
                                            <p className="text-slate-600">Share your artistic influences and inspirations</p>
                                        </div>
                                        <div className="flex items-start space-x-3">
                                            <div className="w-6 h-6 bg-orange-100 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                                                <CheckCircle className="w-4 h-4 text-orange-600" />
                                            </div>
                                            <p className="text-slate-600">Include your creative philosophy and vision</p>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Interactive Demo */}
                        <div className="bg-slate-50 rounded-2xl p-6 border border-slate-200">
                            <div className="space-y-4">
                                <div className="flex items-center space-x-3">
                                    <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-500 rounded-full flex items-center justify-center">
                                        <span className="text-white font-semibold">
                                            {activeUseCase === 'professional' && 'A'}
                                            {activeUseCase === 'personal' && 'M'}
                                            {activeUseCase === 'creative' && 'J'}
                                        </span>
                                    </div>
                                    <div>
                                        <h4 className="font-semibold text-slate-900">
                                            {activeUseCase === 'professional' && "Alex's Echoo"}
                                            {activeUseCase === 'personal' && "Maya's Echoo"}
                                            {activeUseCase === 'creative' && "Jordan's Echoo"}
                                        </h4>
                                        <p className="text-sm text-slate-500">
                                            {activeUseCase === 'professional' && 'Built for networking & clients'}
                                            {activeUseCase === 'personal' && 'Built for family & friends'}
                                            {activeUseCase === 'creative' && 'Built for artistic expression'}
                                        </p>
                                    </div>
                                </div>

                                {activeUseCase === 'professional' && (
                                    <>
                                        <div className="bg-white rounded-lg p-3">
                                            <p className="text-slate-700 text-sm">
                                                "Hi! I'm Alex's professional Echoo. I help companies build products that users actually love. I have 8 years of experience in UX design and product strategy."
                                            </p>
                                        </div>
                                        <div className="bg-blue-50 rounded-lg p-3">
                                            <p className="text-slate-700 text-sm">
                                                "That's exactly the kind of challenge I enjoy tackling. I believe in user-centered design and iterative development. Would you like to hear about my approach to product discovery?"
                                            </p>
                                        </div>
                                    </>
                                )}

                                {activeUseCase === 'personal' && (
                                    <>
                                        <div className="bg-white rounded-lg p-3">
                                            <p className="text-slate-700 text-sm">
                                                "Hi! I'm Maya's personal Echoo. I love connecting with people and learning about their stories. Maya grew up in a small town in Oregon and has always been fascinated by different cultures."
                                            </p>
                                        </div>
                                        <div className="bg-purple-50 rounded-lg p-3">
                                            <p className="text-slate-700 text-sm">
                                                "That reminds me of when Maya first moved to Portland! She always says the best part about new places is discovering the hidden gems that locals love. What's your favorite spot so far?"
                                            </p>
                                        </div>
                                    </>
                                )}

                                {activeUseCase === 'creative' && (
                                    <>
                                        <div className="bg-white rounded-lg p-3">
                                            <p className="text-slate-700 text-sm">
                                                "Hi! I'm Jordan's creative Echoo. Jordan is passionate about the intersection of art and technology. Their work explores how digital tools can enhance rather than replace human creativity."
                                            </p>
                                        </div>
                                        <div className="bg-orange-50 rounded-lg p-3">
                                            <p className="text-slate-700 text-sm">
                                                "Jordan believes every piece of creative work tells a story. What story are you trying to tell through your art? They'd love to hear about your creative process and share some insights from their own journey."
                                            </p>
                                        </div>
                                    </>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </section >
            {/* How It Works Section */}
            < section className="px-6 py-16 bg-slate-50" >
                <div className="max-w-5xl mx-auto">
                    <div className="text-center mb-12">
                        <h2 className="text-3xl font-bold text-slate-900 mb-4">
                            Build your authentic digital representation
                        </h2>
                        <p className="text-lg text-slate-600 max-w-2xl mx-auto">
                            You're in control every step of the way. Share what you want, how you want it.
                        </p>
                    </div>

                    <div className="grid md:grid-cols-3 gap-8">
                        <div className="text-center">
                            <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-blue-600 rounded-2xl flex items-center justify-center mx-auto mb-6">
                                <span className="text-2xl font-bold text-white">1</span>
                            </div>
                            <h3 className="text-xl font-semibold text-slate-900 mb-3">You Share Your Story</h3>
                            <p className="text-slate-600">
                                Tell us about your experiences, values, and stories. You control what information to include.
                            </p>
                        </div>

                        <div className="text-center">
                            <div className="w-16 h-16 bg-gradient-to-br from-purple-500 to-purple-600 rounded-2xl flex items-center justify-center mx-auto mb-6">
                                <span className="text-2xl font-bold text-white">2</span>
                            </div>
                            <h3 className="text-xl font-semibold text-slate-900 mb-3">AI Builds Your Echoo</h3>
                            <p className="text-slate-600">
                                Our AI draws from your unique voice and creates an authentic digital representation that truly speak for you.
                            </p>
                        </div>

                        <div className="text-center">
                            <div className="w-16 h-16 bg-gradient-to-br from-indigo-500 to-indigo-600 rounded-2xl flex items-center justify-center mx-auto mb-6">
                                <span className="text-2xl font-bold text-white">3</span>
                            </div>
                            <h3 className="text-xl font-semibold text-slate-900 mb-3">You Control Access</h3>
                            <p className="text-slate-600">
                                Decide who can chat with your Echoo and customize how it represents you to the world.
                            </p>
                        </div>
                    </div>
                </div>
            </section >



            {/* Simple CTA Section */}
            < section className="px-6 py-16 bg-white" >
                <div className="max-w-3xl mx-auto text-center">
                    <h2 className="text-3xl font-bold text-slate-900 mb-4">
                        Create your Echoo
                    </h2>
                    <p className="text-lg text-slate-600 mb-8">
                        Take control of your digital presence. Always you, always available, always in control.
                    </p>
                    <button
                        onClick={() => setShowWaitlist(true)}
                        className="bg-gradient-to-r from-blue-600 to-purple-600 text-white px-8 py-4 rounded-xl text-lg font-semibold hover:from-blue-700 hover:to-purple-700 transition-all duration-200 shadow-lg hover:shadow-xl"
                    >
                        Join the Waitlist
                    </button>
                </div>
            </section >

            {/* Authentication Modals */}
            {
                showSignIn && (
                    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                        <div className="bg-white rounded-2xl p-8 max-w-md w-full">
                            <div className="flex items-center justify-between mb-6">
                                <h3 className="text-2xl font-bold text-slate-900">Sign In</h3>
                                <button
                                    onClick={closeModals}
                                    className="text-slate-400 hover:text-slate-600"
                                >
                                    ✕
                                </button>
                            </div>

                            <form onSubmit={handleSignIn} className="space-y-4">
                                <div>
                                    <label className="block text-sm font-medium text-slate-700 mb-2">Email</label>
                                    <input
                                        type="email"
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                        required
                                        className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        placeholder="your@email.com"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-slate-700 mb-2">Password</label>
                                    <input
                                        type="password"
                                        value={password}
                                        onChange={(e) => setPassword(e.target.value)}
                                        required
                                        className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        placeholder="••••••••"
                                    />
                                </div>

                                {error && (
                                    <div className="text-red-600 text-sm text-center">
                                        {error}
                                    </div>
                                )}

                                <button
                                    type="submit"
                                    disabled={loading}
                                    className="w-full bg-gradient-to-r from-blue-600 to-purple-600 text-white py-3 rounded-xl font-semibold hover:from-blue-700 hover:to-purple-700 transition-all duration-200 flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    {loading ? (
                                        <Loader2 className="w-5 h-5 animate-spin" />
                                    ) : (
                                        'Sign In'
                                    )}
                                </button>
                            </form>

                            {/* Divider */}
                            <div className="relative my-4">
                                <div className="absolute inset-0 flex items-center">
                                    <div className="w-full border-t border-slate-300" />
                                </div>
                                <div className="relative flex justify-center text-sm">
                                    <span className="px-2 bg-white text-slate-500">Or continue with</span>
                                </div>
                            </div>

                            {/* Google OAuth Button */}
                            <button
                                type="button"
                                onClick={handleGoogleSignIn}
                                disabled={loading}
                                className="w-full flex justify-center items-center py-3 px-4 border border-slate-300 rounded-xl shadow-sm text-sm font-medium text-slate-700 bg-white hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed mb-4"
                            >
                                <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
                                    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                                    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                                    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                                    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
                                </svg>
                                Continue with Google
                            </button>

                            <p className="text-center text-slate-600 text-sm">
                                Don't have an account?
                                <button
                                    onClick={() => {
                                        setShowSignIn(false);
                                        setShowInviteCode(true);
                                    }}
                                    className="text-blue-600 hover:text-blue-700 ml-1"
                                >
                                    Use invite code
                                </button>
                            </p>
                        </div>
                    </div>
                )
            }

            {
                showInviteCode && (
                    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                        <div className="bg-white rounded-2xl p-8 max-w-md w-full">
                            <div className="flex items-center justify-between mb-6">
                                <h3 className="text-2xl font-bold text-slate-900">Join with Invite Code</h3>
                                <button
                                    onClick={closeModals}
                                    className="text-slate-400 hover:text-slate-600"
                                >
                                    ✕
                                </button>
                            </div>

                            <form onSubmit={handleSignUpWithInvite} className="space-y-4">
                                <div>
                                    <label className="block text-sm font-medium text-slate-700 mb-2">Invite Code</label>
                                    <input
                                        type="text"
                                        value={inviteCode}
                                        onChange={(e) => setInviteCodeValue(e.target.value)}
                                        required
                                        className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        placeholder="Enter your invite code"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-slate-700 mb-2">Email</label>
                                    <input
                                        type="email"
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                        required
                                        className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        placeholder="your@email.com"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-slate-700 mb-2">Password</label>
                                    <input
                                        type="password"
                                        value={password}
                                        onChange={(e) => setPassword(e.target.value)}
                                        required
                                        className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        placeholder="••••••••"
                                    />
                                </div>

                                {error && (
                                    <div className="text-red-600 text-sm text-center">
                                        {error}
                                    </div>
                                )}

                                <button
                                    type="submit"
                                    disabled={loading}
                                    className="w-full bg-gradient-to-r from-blue-600 to-purple-600 text-white py-3 rounded-xl font-semibold hover:from-blue-700 hover:to-purple-700 transition-all duration-200 flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    {loading ? (
                                        <Loader2 className="w-5 h-5 animate-spin" />
                                    ) : (
                                        'Create Account'
                                    )}
                                </button>
                            </form>

                            <p className="text-center text-slate-600 text-sm mt-4">
                                Already have an account?
                                <button
                                    onClick={() => {
                                        setShowInviteCode(false);
                                        setShowSignIn(true);
                                    }}
                                    className="text-blue-600 hover:text-blue-700 ml-1"
                                >
                                    Sign in
                                </button>
                            </p>
                        </div>
                    </div>
                )
            }

            {/* Waitlist Modal */}
            {
                showWaitlist && (
                    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                        <div className="bg-white rounded-2xl p-8 max-w-md w-full">
                            <div className="flex items-center justify-between mb-6">
                                <h3 className="text-2xl font-bold text-slate-900">Join the Waitlist</h3>
                                <button
                                    onClick={closeModals}
                                    className="text-slate-400 hover:text-slate-600"
                                >
                                    ✕
                                </button>
                            </div>

                            <p className="text-slate-600 mb-6">
                                Get early access to Echoo.ai and be among the first to create your authentic digital twin.
                            </p>

                            <form onSubmit={handleJoinWaitlist} className="space-y-4">
                                <div>
                                    <label className="block text-sm font-medium text-slate-700 mb-2">Email Address</label>
                                    <input
                                        type="email"
                                        value={waitlistEmail}
                                        onChange={(e) => setWaitlistEmail(e.target.value)}
                                        required
                                        className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        placeholder="your@email.com"
                                    />
                                </div>

                                {error && (
                                    <div className="text-red-600 text-sm text-center">
                                        {error}
                                    </div>
                                )}

                                {successMessage && (
                                    <div className="text-green-600 text-sm text-center">
                                        {successMessage}
                                    </div>
                                )}

                                <button
                                    type="submit"
                                    disabled={loading}
                                    className="w-full bg-gradient-to-r from-blue-600 to-purple-600 text-white py-3 rounded-xl font-semibold hover:from-blue-700 hover:to-purple-700 transition-all duration-200 flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    {loading ? (
                                        <Loader2 className="w-5 h-5 animate-spin" />
                                    ) : (
                                        'Join Waitlist'
                                    )}
                                </button>
                            </form>

                            <p className="text-center text-slate-600 text-sm mt-4">
                                Already have an invite code?
                                <button
                                    onClick={() => {
                                        setShowWaitlist(false);
                                        setShowInviteCode(true);
                                    }}
                                    className="text-blue-600 hover:text-blue-700 ml-1"
                                >
                                    Sign up here
                                </button>
                            </p>
                        </div>
                    </div>
                )
            }

            {/* Simple Footer */}
            <footer className="px-6 py-8 bg-slate-50 border-t border-slate-200">
                <div className="max-w-4xl mx-auto text-center">
                    <div className="flex items-center justify-center space-x-2 mb-4">
                        <div className="w-8 h-8 bg-gradient-to-br from-blue-600 to-purple-600 rounded-lg flex items-center justify-center">
                            <Sparkles className="w-5 h-5 text-white" />
                        </div>
                        <span className="text-lg font-bold text-slate-900">Echoo.ai</span>
                    </div>
                    <p className="text-slate-600 text-sm">
                        © 2025 Echoo.ai Coming soon
                    </p>
                </div>
            </footer>


        </div >
    );
};

export default Landing;


